/**
 * Required fields on a page. One definition shared by the runner (which shows
 * what is missing and stays on the page) and the engine (which refuses to
 * leave the page, so a hand-made request cannot skip a required answer).
 */

/**
 * The element types whose editor settings offer "Required". Kept in step with
 * the checkbox in PageElementProperties.
 */
export const REQUIRABLE_ELEMENT_TYPES = [
  'ShortText',
  'Multiline',
  'Date',
  'Number',
  'Password',
  'MultiChoice',
  'Rating',
] as const;

/**
 * Element types that collect a respondent's answer and write it to
 * `state.vars[el.varId]` when a variable is bound.  An element of one of these
 * types with no varId silently discards every answer.
 *
 * Excluded on purpose:
 *  - Password  — storing passwords is a security anti-pattern; we deliberately
 *                omit it from this list so the linter never suggests binding one.
 *  - ImageUpload — the asset itself is uploaded to S3 regardless of whether a
 *                  variable is bound; the varId stores only the asset-id
 *                  reference, which the author may not need.  The editor already
 *                  shows the variable-key field for ImageUpload, so binding is
 *                  easy when wanted.
 *  - RichText, Button, CollectionDisplay, TableDisplay — these do not collect
 *                  any answer from the respondent.
 */
export const ANSWER_CAPTURING_ELEMENT_TYPES = [
  'ShortText',
  'Multiline',
  'Date',
  'Number',
  'MultiChoice',
  'Rating',
  'YesNo',
] as const;

const REQUIRABLE = new Set<string>(REQUIRABLE_ELEMENT_TYPES);

type ElementLike = { id?: string; type?: string; label?: string; required?: boolean; varId?: string };

/**
 * Where a field's answer travels in a page's input: its bound variable, or,
 * when none is bound, a key from its element id. Unbound answers are not
 * stored anywhere, but they still have to be present to satisfy "Required".
 */
export function fieldKey(element: { id?: string; varId?: string }): string {
  return element.varId || `el:${element.id ?? ''}`;
}

function isEmpty(type: string | undefined, value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  // An unrated Rating reads as 0; any other number (including 0) is an answer.
  if (type === 'Rating' && value === 0) return true;
  return false;
}

export interface MissingField {
  elementId: string;
  label?: string;
}

/** Required fields on the page that `input` leaves empty, in page order. */
export function missingRequiredFields(node: unknown, input: Record<string, unknown> | undefined): MissingField[] {
  const elements = (node as { elements?: unknown })?.elements;
  if (!Array.isArray(elements)) return [];
  return (elements as ElementLike[])
    .filter((el) => el.required === true && REQUIRABLE.has(el.type ?? ''))
    .filter((el) => isEmpty(el.type, input?.[fieldKey(el)]))
    .map((el) => ({ elementId: el.id ?? '', label: el.label }));
}
